import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { ColumnsType } from "@similarweb/pro-logic-components";

import { useUnmount } from "app/custom-hooks/useUnmount";
import type { ContactsTableColumnsExternalProps } from "app/pages/sales-intelligence/sub-modules/contacts-search/configuration/columns/createContactsResultsTableColumns";
import contactToContactIdCompanyPair from "app/pages/sales-intelligence/sub-modules/contacts-search/helpers/contactToContactIdCompanyPair";
import type { IContactSearchErrorBoundaryProps } from "app/pages/sales-intelligence/sub-modules/contacts-search/hoc/withContactSearchErrorBoundary";
import { setSelectedContactEntries } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import type { ContactIdCompanyPair } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import type { WithKey } from "app/pages/sales-intelligence/types/withKey";

import useRemainingRevealCredits from "../../hooks/useRemainingRevealCredits";
import useSingleContactCrmExport from "../../hooks/useSingleContactCrmExport";
import { tableRowsSelectionChangedAction, tableRowsSelectionToggledAction } from "../../store/action-creators";
import { selectCheckedContactIds, selectTableRowsSelectionIsEnabled } from "../../store/selectors";
import type { ContactSearchEntry } from "../../types/common";
import ContactIssueModalContainer from "../ContactIssueModal/ContactIssueModalContainer";
import ContactsExportPreviewModal from "../ContactsTableActionsPanel/ExportContactsToCrm/ContactsExportPreviewModal";
import { LoadingModal } from "../ContactsTableActionsPanel/ExportContactsToCrm/LoadingModal/LoadingModal";
import { RevealBeforeExportModal } from "../RevealBeforeExportModal/RevealBeforeExportModal";
import RevealLimitReachedModalContainer from "../RevealLimitReachedModal/RevealLimitReachedModalContainer";

import ContactsResultsTable from "./ContactsResultsTable";
import type { ContactsResultsTableProps } from "./types";

export interface IContactsResultsTableContainerBaseProps extends IContactSearchErrorBoundaryProps {
    isLoading: boolean;
    onExportItemToExel?: (contactIdCompanyPair: ContactIdCompanyPair) => Promise<void>;
    crmCountries?: readonly number[];
    createColumnsConfigFactory: (props: ContactsTableColumnsExternalProps) => ColumnsType<ContactSearchEntry>;
}

type ContactsResultsTableContainerProps = IContactsResultsTableContainerBaseProps &
    Pick<
        ContactsResultsTableProps,
        | "currentPage"
        | "pageSize"
        | "data"
        | "totalCount"
        | "onPageChange"
        | "onSortingChange"
        | "availableColumns"
        | "isShowCounter"
        | "virtual"
        | "scroll"
        | "onScroll"
    >;

const ContactsResultsTableContainer = ({
    isLoading,
    onExportItemToExel,
    crmCountries,
    createColumnsConfigFactory,
    ...tableProps
}: ContactsResultsTableContainerProps) => {
    const dispatch = useDispatch();

    const isContactsVisible = useSelector(selectIsContactsVisible);
    const checkedContactsIds = useSelector(selectCheckedContactIds);
    const isTableRowsSelectionEnabled = useSelector(selectTableRowsSelectionIsEnabled);
    const remainingCredits = useRemainingRevealCredits();

    const isTableLoadingStateShown = isContactsVisible && isLoading;

    const {
        onExportToCRM,
        isRowMatching,
        isRowPreviewing,
        integrationName,
        onConfirmCrmExport,
        onCancelCrmPreview,
        onCancelCrmLoading,
        isRevealModalOpen,
        onConfirmRevealExport,
        onCloseRevealModal,
    } = useSingleContactCrmExport({ countries: crmCountries });

    useUnmount(() => {
        dispatch(tableRowsSelectionChangedAction([]));
        dispatch(tableRowsSelectionToggledAction(false));
    });

    const handleContactEntriesSelect = useCallback(
        (selectedEntries: WithKey<ContactSearchEntry>[]) => {
            dispatch(setSelectedContactEntries(selectedEntries));
        },
        [dispatch],
    );

    const handleExportItemToExel = (contact: ContactSearchEntry) => {
        void onExportItemToExel?.(contactToContactIdCompanyPair(contact));
    };

    return (
        <>
            <ContactsResultsTable
                {...tableProps}
                isLoading={isTableLoadingStateShown}
                selectedRowKeys={checkedContactsIds}
                isContactsFeatureLocked={!isContactsVisible}
                isTableRowsSelectionEnabled={isTableRowsSelectionEnabled}
                onEntrySelect={handleContactEntriesSelect}
                onExportItemToCRM={onExportToCRM}
                onExportItemToExel={handleExportItemToExel}
                createColumnsConfigFactory={createColumnsConfigFactory}
            />
            <RevealLimitReachedModalContainer />
            <ContactIssueModalContainer />
            <RevealBeforeExportModal
                isOpen={isRevealModalOpen}
                unrevealedCount={1}
                revealedCount={0}
                remainingCredits={remainingCredits}
                onConfirm={onConfirmRevealExport}
                onClose={onCloseRevealModal}
            />
            {integrationName ? (
                <ContactsExportPreviewModal
                    isOpen={isRowPreviewing}
                    integrationName={integrationName}
                    onExport={onConfirmCrmExport}
                    onCancel={onCancelCrmPreview}
                />
            ) : null}
            <LoadingModal
                isOpen={isRowMatching}
                onClose={onCancelCrmLoading}
            />
        </>
    );
};

export default ContactsResultsTableContainer;
